/* eslint-disable no-debugger */
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { errorToast } from '../../../../helpers/toastHelper';
import { isUserOnboarding } from '../../../gamificationTooltip/helpers';
import { useResolutionHandlers } from '../components/Setting/useResolutionPresets';
import { Lock } from '../Lock/Lock';
import { NumberInput } from '../NumberInput';
import styles from './LockedInput.module.scss';
import { Overlay, Tooltip } from "react-bootstrap";

export const LockedInput = ({
  id,
  value: [valueLeft, valueRight],
  lock,
  color,
  onLockToggle,
  className,
  inputClassName,
  onChange,
  onFocus,
  min,
  max,
  minMaxWarning,
  valueFormatter,
  normalizeOnBlur,
  enableSimpleBlur,
  inputContainerClassName,
  onlyIntegers,
  onBlur,
  withArrows,
  normalizeOnExeedsLimit,
  skipEmpty,
  innerRef,
  step,
  inputWidth = '152px',
  size = "lg",
  version,
  InputComponent,
  errorMessage = "",
  ratioParent,
  showToolTip,
  tooltipText
}) => {
  const { handleResolutionBlur, handleSimpleBlur, handleResolutionChange } = useResolutionHandlers({
    keyX: valueLeft.name,
    keyY: valueRight.name,
    defaultX: valueLeft.defaultValue,
    defaultY: valueRight.defaultValue,
    min,
    max,
    data: {
      [valueLeft.name]: valueLeft.value,
      [valueRight.name]: valueRight.value,
      lock,
    },
    onBlur,
    valueFormatter,
    innerRef,
    ratioParent,
    updateCallback: (data, details) => {
      if (!data) return;

      const val1 = data[valueLeft.name];
      const val2 = data[valueRight.name];

      if (minMaxWarning) {
        const val1Incorrect = val1 < min || val1 > max;
        const val2Incorrect = val2 < min || val2 > max;
        if (val1Incorrect || val2Incorrect) {
          errorToast(minMaxWarning, { toastId: `LOCKED_INPUT_${id}` });
        }
      }

      return onChange(data, details);
    },
  });

  // TODO: use own state
  // send only values in onchange event
  // send only values in blur even
  // use hook for handling locked data
  // maybe accept value like => value={{ left: { value: 2, name: 'width' }, right: { value: 5, name: 'height' }}}
  // add possibility for custom validation ->> for example negative, min/max values
  // // add possibility to change values to min/max on blur (this one should be optional)
  // use this input everywhere where we have locked property

  const handleLockToggle = () => {
    if(isUserOnboarding()) {
      return;
    }
    if (lock) {
      // disabling lock should always work
      onLockToggle();
      return;
    }

    // for enabling lock we need to check if there are some values provided
    const value1Valid = typeof valueLeft.value === 'number' && !Number.isNaN(valueLeft.value);
    const value2Valid = typeof valueRight.value === 'number' && !Number.isNaN(valueRight.value);

    if (!value1Valid || !value2Valid) {
      errorToast('Please provide values before locking fields');
      return;
    }

    onLockToggle();
  };

  const handleBlur = (e) => {
    if (normalizeOnBlur) {
      handleResolutionBlur(e);
    }

    if (enableSimpleBlur) {
      handleSimpleBlur(e);
    }
  };

  const handleKeyDown = (e) => {
    var code = e.code || e.key;
    if(code === "Enter")
      e.target.blur();
  }

  const maxLength = `${max}`.length;
  const tooltipTarget = useRef(null);
  const [toolTipOverLock, setToolTipOverLock] = useState(false);

  const mouseMove = (e) => {
    setToolTipOverLock(true);
  }

  const mouseLeave = (e) => {
    setToolTipOverLock(false);
  }
  const isV2 = version === '2';

  return (
    <>
    <div className={cx(styles.root, className, isV2 && styles.v2)}>
      <div
        className={cx('resolution-input', styles.inputContainer, inputContainerClassName)}
        style={{ flex: `0 0 ${inputWidth}` }}>
        <label className="form-label">{valueLeft.label}</label>
        <NumberInput
          type="text"
          className={cx('form-control', inputClassName)}
          name={valueLeft.name}
          value={valueLeft.value || ""}
          onBlur={handleBlur}
          onChange={handleResolutionChange}
          onlyIntegers={onlyIntegers}
          withArrows={withArrows}
          disabled={valueLeft.disabled}
          min={min}
          max={max}
          step={step}
          size={size}
          InputComponent={InputComponent}
          normalizeOnExeedsLimit={normalizeOnExeedsLimit}
          onFocus={onFocus}
          skipEmpty={skipEmpty}
          onKeyPress={handleKeyDown}
          version={version}
        />
      </div>
      <Overlay
        show={showToolTip && toolTipOverLock}
        placement="top"
        target={tooltipTarget.current}
      >
        {(props) => (
          <Tooltip {...props} className={styles.tooltip}>
            {tooltipText}
          </Tooltip>
        )}
      </Overlay>
      <div
        ref={tooltipTarget}
        className={cx('lock-icon d-flex align-items-center justify-content-center', styles.lock)} 
        onMouseLeave={(e) => mouseLeave(e)}
        onMouseMove={(e) => mouseMove(e)}>
        <Lock active={lock} onClick={handleLockToggle} color={color} />
      </div>
      <div
        className={cx('resolution-input', styles.inputContainer, inputContainerClassName)}
        style={{ flex: `0 0 ${inputWidth}` }}>
        <label className="form-label">{valueRight.label}</label>
        <NumberInput
          type="text"
          className={cx('form-control', inputClassName)}
          name={valueRight.name}
          value={valueRight.value || ''}
          onBlur={handleBlur}
          onChange={handleResolutionChange}
          onlyIntegers={onlyIntegers}
          withArrows={withArrows}
          disabled={valueRight.disabled}
          min={min}
          max={max}
          size={size}
          step={step}
          InputComponent={InputComponent}
          normalizeOnExeedsLimit={normalizeOnExeedsLimit}
          onFocus={onFocus}
          skipEmpty={skipEmpty}
          version={version}
          onKeyPress={handleKeyDown}
        />
      </div>
    </div>
    {errorMessage ? <span className={styles.errorMessage}>{errorMessage}</span> : null}
    </>
  );
};

export const UndoRedoLockedInput = ({
  undoRedoAction,
  undoRedoData,
  onChange,
  onFocus,
  ...rest
}) => {
  const valueRef = useRef(null);

  const handleFocus = (e) => {
    valueRef.current = rest.value;
    onFocus?.(e);
  };

  const handleChange = (data, details) => {
    if (details.type === 'blur') {
      const [valueLeft, valueRight] = valueRef.current;

      undoRedoAction?.({
        prevValue: {
          [valueLeft.name]: valueLeft.value,
          [valueRight.name]: valueRight.value,
        },
        nextValue: data,
        data: undoRedoData,
      });
    } else if (details.triggeredBy === 'arrowUp' || details.triggeredBy === 'arrowDown') {
      const [valueLeft, valueRight] = rest.value;
      undoRedoAction?.({
        prevValue: {
          [valueLeft.name]: valueLeft.value,
          [valueRight.name]: valueRight.value,
        },
        nextValue: data,
        data: undoRedoData,
      });
    }

    onChange?.(data, details);
  };

  return <LockedInput {...rest} onChange={handleChange} onFocus={handleFocus} />;
};
